import type {
  RouteLocationNormalized,
  RouteLocationRaw,
} from 'vue-router'
import type { Role } from '@/api/client'
import { canAdminister } from '@/lib/capabilities'

/**
 * Pure redirect-decision logic for the auth guard.
 *
 * Given the target route and the current auth state, decide whether the
 * navigation is allowed (`true`) or must be redirected (a route target).
 * This holds NO async/boot-probe logic — that lives in the `beforeEach`
 * callback in `router/index.ts` — so it can be unit-tested without mounting
 * a router or a Pinia store. It lives in its own module (not `index.ts`) so
 * importing it never triggers `createRouter` / `createWebHistory`.
 *
 * Rules (checked in this order):
 *  - authenticated + a forced password change pending → confined to
 *    `/change-password`; every other target (even public ones like `/login`)
 *    bounces there until the password is changed. Logout is an action, not a
 *    route, so it stays reachable via the topbar;
 *  - `to.meta.public` → allowed, except an authenticated user on the login
 *    route is bounced to the dashboard;
 *  - not authenticated on a private route → redirect to `/login`, carrying
 *    the original target in `query.redirect`;
 *  - `to.meta.requiresPlatformAdmin` and the session is not a platform account
 *    → redirect to the dashboard;
 *  - `to.meta.requiresAdmin` and the session's role is below admin → redirect
 *    to the dashboard;
 *  - authenticated on a private route → allowed.
 *
 * 🔴 THE TWO META FLAGS ARE DIFFERENT AXES, NOT TWO HEIGHTS ON ONE LADDER.
 * `requiresAdmin` asks about rank inside a CUSTOMER's hierarchy;
 * `requiresPlatformAdmin` asks whether the session is OURS, and no rank
 * produces it — the CLI creates a platform account as `viewer` precisely so
 * that nothing can confuse the two. A route carrying BOTH would be
 * unreachable by construction, which `guard.spec.ts` asserts rather than
 * leaves to be discovered.
 *
 * THE ROLE CHECK REDIRECTS RATHER THAN RENDERING A REFUSAL, deliberately. An
 * operator typing `/team` has not been caught doing anything — the route simply
 * is not part of their product, and a "403 Forbidden" screen describes a
 * malfunction to somebody whose account is working exactly as configured. They
 * land on the dashboard, which is where the link they do not have would never
 * have taken them from. (The nav entry is hidden for the same reason; this is
 * the half that also covers a typed URL, a bookmark, and a shared link.)
 *
 * ⚠️ THIS IS NOT WHAT PROTECTS THE DATA. Every `/api/users` route declares an
 * ADMIN floor server-side and answers 403 on its own; a user who edits the
 * bundle to reach the view gets a screen full of failed requests, not a team
 * list. Removing the backend gate because "the router handles it" is the
 * mistake this paragraph exists to prevent.
 */
export function resolveRoute(
  to: RouteLocationNormalized,
  isAuthenticated: boolean,
  mustChangePassword = false,
  // Defaults to `null` — "no role known" — so a route added with
  // `meta.requiresAdmin` is closed by any caller that has not been taught to
  // pass the role, rather than open by omission.
  role: Role | null = null,
  // Defaults to `false` for the same reason `role` defaults to `null`: a route
  // added with `meta.requiresPlatformAdmin` is closed by any caller that has
  // not been taught to pass this, rather than open by omission. Fail-closed on
  // the axis where the alternative is a customer reaching our screens.
  isPlatformAdmin = false,
): true | RouteLocationRaw {
  // A user who must change their password can reach nothing but that screen.
  if (isAuthenticated && mustChangePassword && to.name !== 'change-password') {
    return { name: 'change-password' }
  }

  if (to.meta['public']) {
    // Authenticated users have no business on /login — bounce them home.
    if (to.name === 'login' && isAuthenticated) {
      return { name: 'dashboard' }
    }
    return true
  }

  if (!isAuthenticated) {
    return { name: 'login', query: { redirect: to.fullPath } }
  }

  // Checked AFTER authentication on purpose: an anonymous visitor to an
  // admin-only URL must be offered the login screen with their target
  // preserved, not bounced to a dashboard they cannot see either.
  // Checked BEFORE the role floor, and independently of it: this is not a
  // higher rung, it is the other axis. Note that an impersonating platform
  // admin fails this too — the backend reports `is_platform_admin: false` for
  // the length of the window and every platform route 403s, so bouncing them
  // out of the panel is the navigation agreeing with the API rather than a
  // special case anybody had to write.
  if (to.meta['requiresPlatformAdmin'] && !isPlatformAdmin) {
    return { name: 'dashboard' }
  }

  if (to.meta['requiresAdmin'] && !canAdminister(role)) {
    return { name: 'dashboard' }
  }

  return true
}
